import { Controller } from "@hotwired/stimulus";

// Controller for schedule date/time selection and validation
export default class extends Controller {
  static targets = ["timeSelect", "container"];

  connect() {
    // Find and attach to the main payment form for validation
    this.paymentForm = document.getElementById("payment-form");
    if (this.paymentForm) {
      this.paymentForm.addEventListener("submit", this.validateBeforeSubmit.bind(this));
    }

    // Initialize flatpickr on connect
    this.instantiateFlatpickr();

    // Check initial state and mark invalid if no times available
    this.checkAndMarkValidity();
  }

  disconnect() {
    if (this.paymentForm) {
      this.paymentForm.removeEventListener("submit", this.validateBeforeSubmit.bind(this));
    }
  }

  // Check and mark the time select as valid/invalid based on available options
  checkAndMarkValidity() {
    if (!this.hasTimeSelectTarget) return;

    const timeSelect = this.timeSelectTarget;

    // Check if we have valid options (not empty, not disabled)
    const hasValidOption = Array.from(timeSelect.options).some(
      opt => opt.value && opt.value !== "" && !opt.disabled
    );

    if (!hasValidOption) {
      // Mark as invalid and disable if no valid options
      timeSelect.classList.add("is-invalid");
      timeSelect.setCustomValidity("No times available");

      // Show error message
      let errorMsg = timeSelect.parentElement.querySelector(".invalid-feedback");
      if (!errorMsg) {
        errorMsg = document.createElement("div");
        errorMsg.className = "invalid-feedback d-block";
        errorMsg.textContent = "No times are currently available for this date. Please select a different date or contact the restaurant.";
        timeSelect.parentElement.appendChild(errorMsg);
      }
    } else {
      // Clear invalid state if we have valid options
      timeSelect.classList.remove("is-invalid");
      timeSelect.setCustomValidity("");

      const errorMsg = timeSelect.parentElement.querySelector(".invalid-feedback");
      if (errorMsg) errorMsg.remove();
    }
  }

  // Validate that a valid time is selected before form submission
  validateBeforeSubmit(event) {
    if (!this.hasTimeSelectTarget) return true;

    // Uber Direct delivery orders are ASAP-only: the scheduling section is
    // hidden and its value overridden, so don't block submission on it.
    const scheduleSection = document.getElementById("schedule_section");
    if (scheduleSection?.classList.contains("hide")) return true;

    const timeSelect = this.timeSelectTarget;
    const selectedValue = timeSelect.value;

    // Check if we have valid options
    const hasValidOption = Array.from(timeSelect.options).some(
      opt => opt.value && opt.value !== "" && !opt.disabled
    );

    if (!hasValidOption || !selectedValue || selectedValue === "") {
      event.preventDefault();
      event.stopImmediatePropagation();

      // Show error
      timeSelect.classList.add("is-invalid");
      timeSelect.setCustomValidity("Please select a valid time");

      let errorMsg = timeSelect.parentElement.querySelector(".invalid-feedback");
      if (!errorMsg) {
        errorMsg = document.createElement("div");
        errorMsg.className = "invalid-feedback d-block";
        errorMsg.textContent = "Please select a valid time. If no times are available, try a different date or contact the restaurant.";
        timeSelect.parentElement.appendChild(errorMsg);
      }

      timeSelect.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }

    // Clear any validation errors
    timeSelect.classList.remove("is-invalid");
    timeSelect.setCustomValidity("");
    const errorMsg = timeSelect.parentElement.querySelector(".invalid-feedback");
    if (errorMsg) errorMsg.remove();

    return true;
  }

  handleDateChange(event) {
    const dateInput = event.currentTarget;
    const selectedDate = dateInput.value;
    const targetUrl = dateInput.dataset.targetUrl;
    const paramName = dateInput.dataset.paramName || "date";

    if (!targetUrl || !selectedDate) {
      console.error("Missing required data for date change handling");
      return;
    }

    const queryUrl = `${targetUrl}?${paramName}=${selectedDate}`;

    // Get the container element (the element with the controller)
    const container = this.hasContainerTarget ? this.containerTarget : this.element;

    const timeSelector =
      container.querySelector("#catering_time") ||
      container.querySelector("#pickup_time");
    if (timeSelector) {
      timeSelector.disabled = true;
      timeSelector.innerHTML = "<option>Loading times...</option>";
    }

    fetch(queryUrl, {
      method: "GET",
      headers: {
        Accept: "text/html, application/xhtml+xml",
      },
    })
      .then((response) => {
        if (!response.ok) {
          console.error("AJAX request failed:", response.status);
          throw new Error("Network response was not ok");
        }
        return response.text();
      })
      .then((html) => {
        const dateValue = selectedDate;

        container.innerHTML = html;

        this.instantiateFlatpickr(container);

        const newDateInput =
          container.querySelector("#catering_date_selector") ||
          container.querySelector("#pickup_date_selector");
        if (newDateInput) {
          newDateInput.value = dateValue;

          if (newDateInput._flatpickr) {
            newDateInput._flatpickr.setDate(dateValue);
          }
        }

        this.checkAndMarkValidity();
      })
      .catch((error) => {
        console.error("Error during AJAX request:", error);
      });
  }

  instantiateFlatpickr(container = null) {
    const targetContainer = container || (this.hasContainerTarget ? this.containerTarget : this.element);
    targetContainer.querySelectorAll(".flatpickr").forEach((element) => {
      flatpickr(element, {
        altInput: true,
        altFormat: "F j, Y",
        dateFormat: "Y-m-d",
        disableMobile: true,
        minDate: element.dataset.minDate || "today",
        defaultDate: element.value || element.dataset.defaultDate || "today",
      });
    });
  }
}
